import React, {FC, ReactNode, useMemo} from 'react';

import {useEventHandler} from '../../../../../libs/shared/core/src/helpers/react/hooks/useEventHandler';
import {
  VisualBasicCollapsedActivity,
  VisualBasicCollapsedActivityTypesEnum,
} from '../../../../../srcCommon/inboxes/viewer/activity/visualActivity/visualBasicCollapsedActivity';
import {SandboxScenariosEnum} from '../../../../helpers/sandbox/sandboxScenarioHelpers';
import {RulesIntroEducationTip} from './rulesIntroEducationTip';
import {
  SandboxEducationTipCoordinator,
  useDismissSandboxEducationTip,
} from './sandboxEducationTipCoordinator';

/*
 * Props.
 */

interface RulesIntroEducationTipCoordinatorBaseProps {
  conversationId: number;
  isActivityGroupHeaderCollapsed: boolean;
  isEligibleActivityGroup: boolean;
}

interface RulesIntroEducationTipCoordinatorProps extends RulesIntroEducationTipCoordinatorBaseProps {
  children?: ReactNode;
}

/*
 * Constants.
 */

const step = 'click-to-see-rules';
const brandonDemoTeammateAlias = 'brandon_demo';

// The original Brandon demo teammate alias, some older companies will have this.
const brandonDemoTeammateAliasSandboxV1 = 'brandon_kang_demo';

/*
 * Component.
 */

export const RulesIntroEducationTipCoordinator: FC<RulesIntroEducationTipCoordinatorProps> = (props) => {
  // As an optimization, render only on collapsed activity group headers.
  if (!props.isActivityGroupHeaderCollapsed) {
    return <>{props.children}</>;
  }

  return (
    <SandboxEducationTipCoordinator
      conversationId={props.conversationId}
      scenario={SandboxScenariosEnum.SCENARIO3_ACT2}
      step={step}
      isEligible={props.isEligibleActivityGroup}
      EducationTip={RulesIntroEducationTip}
    >
      {props.children}
    </SandboxEducationTipCoordinator>
  );
};

/*
 * Hooks.
 */

export function useRulesIntroEducationTip(
  conversationId: number,
  isHeaderExpanded: boolean,
  collapsedHeaderActivities: ReadonlyArray<VisualBasicCollapsedActivity>,
) {
  const onDismiss = useDismissSandboxEducationTip<SandboxScenariosEnum.SCENARIO3_ACT2>(conversationId, step);

  const isActivityGroupHeaderCollapsed = !isHeaderExpanded;

  const demoTeammateAliases = new Set([brandonDemoTeammateAlias, brandonDemoTeammateAliasSandboxV1]);

  const isEligibleActivityGroup =
    collapsedHeaderActivities.some(
      (activity) => activity.type === VisualBasicCollapsedActivityTypesEnum.TAG_ADDED_BY_RULE,
    ) &&
    collapsedHeaderActivities.some(
      (activity) =>
        activity.type === VisualBasicCollapsedActivityTypesEnum.ASSIGNED_TO_TEAMMATE_BY_RULE &&
        demoTeammateAliases.has(activity.assignee.alias),
    );

  const rulesIntroEducationTipCoordinatorProps: RulesIntroEducationTipCoordinatorBaseProps = useMemo(
    () => ({conversationId, isActivityGroupHeaderCollapsed, isEligibleActivityGroup}),
    [conversationId, isActivityGroupHeaderCollapsed, isEligibleActivityGroup],
  );

  const onHeaderClick = useEventHandler(() => {
    if (isActivityGroupHeaderCollapsed && isEligibleActivityGroup) {
      onDismiss();
    }
  });

  return {
    rulesIntroEducationTipCoordinatorProps,
    onHeaderClick,
  };
}
